import React, {useState, useEffect, useCallback} from 'react';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import {API_ENDPOINTS} from '../../../apiConfig';
import API from '../../../utils/API';
import Forms from '../../../components/Forms';
import EditIconButton from "../../../components/Forms/EditIconButton";
import DeleteIconButton from "../../../components/Forms/DeleteIconButton";
import {useModal} from '../../../contexts/ModalContext';
import CustomerModal from './CustomerModal';
import AdminDataView from "../../../components/AdminDataView";

const Customers = () => {
    const {t} = useTranslation();
    const {openModal, confirm} = useModal();
    const [customers, setCustomers] = useState([]);

    const fetchCustomers = useCallback(async () => {
        try {
            const res = await API.get(API_ENDPOINTS.customer.list);
            setCustomers(res.data || []);
        } catch (e) {
            console.error('Failed to fetch customers:', e);
        }
    }, []);

    useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

    const confirmDeleteCustomer = async (customer) => {
        const label = customer?.type === 'company'
            ? customer?.company?.name
            : [customer?.person?.name, customer?.person?.surname].filter(Boolean).join(' ');
        const ok = await confirm(t('customers.confirmDeletionMessage', { name: label || t('unknown') }), {});
        if (ok === true) {
            try {
                await API.delete(API_ENDPOINTS.customer.delete(customer._id));
                setCustomers(prev => prev.filter(c => c._id !== customer._id));
            } catch (e) {
                console.error('Failed to delete customer:', e);
            }
        }
    };

    const openCustomerModal = (customer) => {
        openModal(
            CustomerModal,
            { customer, onSaved: fetchCustomers },
            { title: customer ? t('customers.editCustomer') : t('customers.addCustomer') }
        );
    };

    const columns = [
        {
            key: 'display',
            name: t('customers.customer'),
            width: '35%',
            renderCell: ({row}) => {
                const label = row?.type === 'company'
                    ? row?.company?.name
                    : [row?.person?.name, row?.person?.surname].filter(Boolean).join(' ');
                return (
                    <div className="truncate">
                        <div className="font-medium">{label || t('unknown')}</div>
                        {row?.type === 'company' && (row?.company?.ico || row?.company?.dic) && (
                            <div className="text-xs opacity-70">
                                {row?.company?.ico ? `IČO: ${row.company.ico}` : ''}
                                {row?.company?.dic ? ` • DIČ: ${row.company.dic}` : ''}
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            key: 'email',
            name: 'Email',
            width: '25%',
            renderCell: ({row}) => row?.emails?.[0]?.value || '-'
        },
        {
            key: 'phone',
            name: t('phone'),
            width: '15%',
            renderCell: ({row}) => row?.phones?.[0]?.value || '-'
        },
        {
            key: 'blacklisted',
            name: t('customers.blacklisted'),
            width: '10%',
            renderCell: ({row}) => row?.risk?.blacklisted ? t('yes') : t('no')
        },
        {
            key: 'createdAt',
            name: t('createdAt'),
            width: '15%',
            renderCell: ({row}) => row?.createdAt ? moment(row.createdAt).format('DD.MM.YYYY HH:mm') : '-'
        },
        {
            key: 'actions',
            name: 'Akce',
            width: '10%',
            renderCell: ({row}) => (
                <div className="flex items-center justify-end h-full w-full">
                    <EditIconButton title="Upravit zákazníka" onClick={() => openCustomerModal(row)}/>
                    <DeleteIconButton title="Smazat zákazníka" onClick={() => confirmDeleteCustomer(row)}/>
                </div>
            )
        }
    ].map(c => ({...c, resizable: true}));

    return (
        <div>
            <Forms.Button label={t('customers.addCustomer')} onClick={() => openCustomerModal()} className="mb-8"/>
            <AdminDataView section="customers" rows={customers} columns={columns}/>
        </div>
    );
};

export default Customers;
